import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search,
  Copy,
  MessageSquareHeart,
  Inbox,
  Download,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useVideoFeatures } from '@/store/videoFeatures';
import { useVideoFeedback } from '@/hooks/useVideoFeedback';
import { FeedbackResponse } from '@/services/videoFeedbackService';

interface Props {
  videoId: string | null;
}

const RATING_LABELS: Record<number, string> = {
  get 1() { return translateInterfaceText("Muito ruim"); },
  2: 'Ruim',
  3: 'Neutro',
  4: 'Bom',
  5: 'Excelente',
};

function formatAvg(avg: number) {
  if (!avg) return '—/5';
  return `${avg.toFixed(1).replace('.', ',')}/5`;
}

function formatDate(d?: string | Date) {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!date || isNaN(date.getTime())) return String(d);
  return date.toLocaleString(getInterfaceLanguage(), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ratingDisplay(ratingType: string, value: number) {
  if (ratingType === 'stars') return '★'.repeat(value) + '☆'.repeat(5 - value);
  if (ratingType === 'emoji') return ['😡', '😕', '😐', '🙂', '😄'][value - 1] || '—';
  return `${value}/5`;
}

function toneColor(rating: number): string {
  if (rating >= 4) return '#4B7BFF';
  if (rating === 3) return '#F5B400';
  return '#EF6B6B';
}

function KpiCard({ title, value, subtitle }: { title: string; value: string | number; subtitle: string }) {
  useLanguage();
  return (
    <div className="rounded-[16px] border border-[#E5E7EB] bg-white p-4 min-w-0 overflow-hidden">
      <p className="text-[12px] text-[#6B7280] mb-2 truncate">{title}</p>
      <p className="text-[22px] font-semibold text-[#1A1A1A] tabular-nums truncate">{value}</p>
      <p className="text-[11.5px] text-[#9CA3AF] mt-1.5 truncate">{subtitle}</p>
    </div>
  );
}

export const FeedbackTabContent: React.FC<Props> = ({ videoId }) => {
  useLanguage();
  const features = useVideoFeatures((s) => s.features);
  const setCurrentFeature = useVideoFeatures((s) => s.setCurrentFeature);

  const fb = features.videoFeedback;
  const enabled = !!fb?.enabled;
  const ratingType = (fb?.ratingType || 'stars') as 'emoji' | 'stars' | 'nps';

  const [range] = useState(() => ({
    inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    fim: new Date().toISOString().split('T')[0],
  }));
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<FeedbackResponse | null>(null);

  const { metrics, responses, isLoadingMetrics, refetchMetrics, fetchResponses, exportData } =
    useVideoFeedback({ videoId: videoId || '', enabled: enabled && !!videoId });

  const load = useCallback(() => {
    if (!videoId) return;
    refetchMetrics(range.inicio, range.fim);
    fetchResponses(1, 50, 'all', query || undefined);
  }, [videoId, range, query, refetchMetrics, fetchResponses]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const distribution = useMemo(() => {
    const total = metrics?.total_responses || 0;
    return [5, 4, 3, 2, 1].map((rating) => {
      const count = metrics?.distribution?.[String(rating)]?.count || 0;
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        label: `${rating} ${RATING_LABELS[rating]}`,
        value: count,
        pct,
        tone: toneColor(rating),
      };
    });
  }, [metrics]);

  const timeline = useMemo(
    () => (metrics?.evolution || []).map((e) => ({ date: e.date, avg: e.average_rating })),
    [metrics]
  );

  if (!enabled) {
    return (
      <div className="rounded-[16px] border border-dashed border-[#C7D2FE] bg-[#F8FAFF] py-16 px-6 text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-[14px] bg-[#EEF2FF] flex items-center justify-center">
          <MessageSquareHeart className="w-6 h-6 text-[#4B7BFF]" />
        </div>
        <h3 className="text-[16px] font-semibold text-[#1A1A1A] mb-1.5">{translateInterfaceText("Nenhum feedback recebido ainda")}</h3>
        <p className="text-[13px] text-[#6B7280] max-w-[460px] mx-auto leading-relaxed mb-5">
          {translateInterfaceText("Quando espectadores avaliarem este vídeo, as respostas aparecerão aqui com nota média, comentários e distribuição das avaliações.")}</p>
        <button
          type="button"
          onClick={() => setCurrentFeature('videoFeedback')}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#4B7BFF] text-white text-[13px] font-medium hover:bg-[#3D6AE8] transition-colors"
        >
          {translateInterfaceText("Configurar Feedback do Vídeo")}</button>
      </div>
    );
  }

  if (!videoId) {
    return (
      <div className="rounded-[16px] border border-[#E5E7EB] bg-white p-8 text-center">
        <p className="text-[13px] text-[#6B7280]">{translateInterfaceText("Salve o vídeo para visualizar as métricas de feedback.")}</p>
      </div>
    );
  }

  if (isLoadingMetrics) {
    return (
      <div className="rounded-[16px] border border-[#E5E7EB] bg-white p-8 text-center">
        <RefreshCw className="w-6 h-6 text-[#4B7BFF] animate-spin mx-auto mb-3" />
        <p className="text-[13px] text-[#6B7280]">{translateInterfaceText("Carregando métricas...")}</p>
      </div>
    );
  }

  const total = metrics?.total_responses || 0;

  if (total === 0) {
    return (
      <div className="rounded-[16px] border border-[#E5E7EB] bg-white p-8 text-center space-y-2">
        <MessageSquareHeart className="w-8 h-8 text-[#D1D5DB] mx-auto" />
        <p className="text-[13px] font-medium text-[#1A1A1A]">{translateInterfaceText("Nenhum feedback recebido ainda")}</p>
        <p className="text-[12.5px] text-[#6B7280] leading-relaxed">
          {translateInterfaceText("Compartilhe o vídeo para começar a coletar avaliações dos espectadores.")}</p>
      </div>
    );
  }

  const commentList = (responses?.responses || []).filter((r) => r.comment).slice(0, 10);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard title={translateInterfaceText("Nota média")} value={formatAvg(metrics?.average_rating || 0)} subtitle={translateInterfaceText("Média geral")} />
        <KpiCard title={translateInterfaceText("Respostas")} value={total} subtitle={translateInterfaceText("Avaliações")} />
        <KpiCard
          title={translateInterfaceText("Taxa")}
          value={`${Math.round((metrics?.response_rate || 0) * 100)}%`}
          subtitle={translateInterfaceText("Espectadores")}
        />
        <KpiCard
          title={translateInterfaceText("Satisfação")}
          value={`${Math.round((metrics?.positive_satisfaction || 0) * 100)}%`}
          subtitle={translateInterfaceText("Avaliações")}
        />
      </div>

      {/* Distribuição */}
      <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5">
        <header className="mb-4">
          <h3 className="text-[14px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Distribuição das avaliações")}</h3>
        </header>
        <div className="space-y-2.5">
          {distribution.map((b) => (
            <div key={b.label} className="flex items-center gap-3">
              <span className="text-[12.5px] text-[#374151] w-[140px] shrink-0">{b.label}</span>
              <div className="flex-1 h-2.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${b.pct}%`, background: b.tone }}
                />
              </div>
              <span className="tabular-nums w-[88px] text-right flex items-baseline justify-end gap-1.5">
                <span className="text-[13px] font-semibold text-[#111827]">{b.pct}%</span>
                <span className="text-[11.5px] text-[#9CA3AF]">{b.value}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Evolução */}
      <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5">
        <header className="mb-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
            <TrendingUp className="w-4 h-4 text-[#4B7BFF]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Evolução da nota média")}</h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5 truncate">
              {translateInterfaceText("Percepção do conteúdo ao longo do período.")}</p>
          </div>
        </header>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <AreaChart data={timeline} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="feedbackAvgGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4B7BFF" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#4B7BFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                minTickGap={24}
                tickFormatter={(v: string) => {
                  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
                  if (!m) return String(v);
                  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                  return `${m[3]} ${months[parseInt(m[2], 10) - 1]}`;
                }}
              />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} domain={[0, 5]} width={28} />
              <ReTooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 12, boxShadow: '0 4px 16px rgba(15,23,42,0.06)' }}
                formatter={(v: number) => [`${v}/5`, 'Nota média']}
                labelFormatter={(l) => {
                  const d = new Date(l as string);
                  if (isNaN(d.getTime())) return String(l);
                  return d.toLocaleDateString(getInterfaceLanguage(), { day: '2-digit', month: 'long', year: 'numeric' });
                }}
              />
              <Area
                type="monotone"
                dataKey="avg"
                stroke="#4B7BFF"
                strokeWidth={2.25}
                fill="url(#feedbackAvgGradient)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: '#4B7BFF' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Comentários */}
      <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5">
        <header className="mb-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
            <MessageSquareHeart className="w-4 h-4 text-[#4B7BFF]" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Comentários recentes")}</h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5">
              {translateInterfaceText("Top 10 comentários enviados após a avaliação.")}</p>
          </div>
        </header>

        <div className="flex items-center gap-2.5 w-full mt-3.5 flex-col sm:flex-row sm:items-stretch">
          <div className="relative flex-1 w-full">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder={translateInterfaceText("Buscar comentário")}
              className="pl-8 h-10 text-[12.5px] w-full rounded-[12px]"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1.5 h-10 px-4 min-w-[88px] rounded-[12px] border border-[#E5E7EB] bg-white text-[12.5px] font-medium text-[#374151] hover:bg-[#F9FAFB] whitespace-nowrap w-full sm:w-auto"
              >
                <Download className="w-3.5 h-3.5" />
                {translateInterfaceText("Baixar")}</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem onClick={() => exportData('excel', range.inicio, range.fim)} className="bg-[#2563EB] text-white focus:bg-[#1E50C7] focus:text-white">
                {translateInterfaceText("Excel (.xlsx)")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData('csv', range.inicio, range.fim)} className="bg-[#2563EB] text-white focus:bg-[#1E50C7] focus:text-white mt-1">
                {translateInterfaceText("CSV (.csv)")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-2.5 mt-4">
          {commentList.length > 0 ? (
            commentList.map((r) => (
              <button
                key={r.id_feedback}
                type="button"
                onClick={() => setDetail(r)}
                className="w-full text-left rounded-[10px] border border-[#D9DEE7] p-3.5 bg-white hover:border-[#C7D2FE] transition-colors"
              >
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[12.5px] font-medium text-[#1A1A1A]">
                      {r.viewer_name || translateInterfaceText("Anônimo")}
                    </span>
                    <span className="text-[12px] text-[#F5B400]">
                      {ratingDisplay(r.rating_type || ratingType, r.rating_value)}
                    </span>
                  </div>
                  <span className="text-[11px] text-[#9CA3AF]">{formatDate(r.createdAt)}</span>
                </div>
                <p className="text-[13px] text-[#374151] leading-relaxed">"{r.comment}"</p>
              </button>
            ))
          ) : (
            <div className="text-center py-8 text-[12.5px] text-[#9CA3AF] flex flex-col items-center gap-2">
              <Inbox className="w-5 h-5" />
              {translateInterfaceText("Nenhum comentário encontrado com esse filtro.")}</div>
          )}
        </div>
      </section>

      {/* Detalhe */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[560px] max-w-[95vw] p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-[16px]">{translateInterfaceText("Detalhes do feedback")}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="px-6 py-5 space-y-3 text-[13px] max-h-[60vh] overflow-y-auto">
              {[
                [translateInterfaceText("Avaliação"), `${ratingDisplay(detail.rating_type || ratingType, detail.rating_value)} · ${detail.rating_label || RATING_LABELS[detail.rating_value] || '—'}`],
                [translateInterfaceText("Comentário"), detail.comment || '—'],
                [translateInterfaceText("Nome"), detail.viewer_name || '—'],
                [translateInterfaceText("E-mail"), detail.viewer_email || '—'],
                [translateInterfaceText("WhatsApp"), detail.viewer_whatsapp || '—'],
                [translateInterfaceText("Data"), formatDate(detail.createdAt)],
                [translateInterfaceText("Tempo assistido"), detail.watched_seconds != null ? `${Math.floor(detail.watched_seconds / 60)} min ${detail.watched_seconds % 60} s` : '—'],
                [translateInterfaceText("Percentual assistido"), detail.watched_percent != null ? `${detail.watched_percent}%` : '—'],
                [translateInterfaceText("Gatilho"), detail.trigger_type || '—'],
                [translateInterfaceText("Dispositivo"), detail.device_type || '—'],
                [translateInterfaceText("Navegador"), detail.browser || '—'],
                [translateInterfaceText("Cidade"), detail.city || '—'],
                [translateInterfaceText("País"), detail.country || '—'],
                [translateInterfaceText("URL de referência"), detail.referrer_url || '—'],
                ['UTM Source', detail.utm_source || '—'],
                ['UTM Medium', detail.utm_medium || '—'],
                ['UTM Campaign', detail.utm_campaign || '—'],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[140px_1fr] gap-3">
                  <span className="text-[#6B7280]">{k}</span>
                  <span className="text-[#1A1A1A] break-words">{v}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="px-6 py-3 border-t gap-2">
            {detail && (
              <Button
                variant="outline"
                onClick={() => navigator.clipboard.writeText(JSON.stringify(detail, null, 2))}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                {translateInterfaceText("Copiar dados")}</Button>
            )}
            <DialogClose asChild>
              <Button>{translateInterfaceText("Fechar")}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackTabContent;
